import {IssueNewContent, type IssueNewContentRepository} from '@github-ui/issues-shared/IssueNewContent'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {graphql, type PreloadedQuery, usePreloadedQuery} from 'react-relay'

import type {InternalIssueNewPageUrlArgumentsMetadataQuery} from './__generated__/InternalIssueNewPageUrlArgumentsMetadataQuery.graphql'

export type InternalIssueNewPageUrlArgumentsMetadataFragment = IssueNewContentRepository

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const InternalIssueNewPageUrlArgumentsMetadata = graphql`
  query InternalIssueNewPageUrlArgumentsMetadataQuery(
    $owner: String!
    $name: String!
    $withAssignees: Boolean = false
    $assigneeLogins: String = ""
    $withLabels: Boolean = false
    $labelNames: String = ""
    $withMilestone: Boolean = false
    $milestoneTitle: String = ""
    $type: String = ""
    $withType: Boolean = false
    $withProjects: Boolean = false
    $projectNumbers: [Int!] = []
    $withTriagePermission: Boolean = false
    $discussionNumber: Int = 0
    $includeDiscussion: Boolean = false
    $templateFilter: String = ""
    $withTemplate: Boolean = false
  ) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      databaseId
      name
      nameWithOwner
      owner {
        databaseId
        login
        avatarUrl(size: 64)
        issueTypesEnabled
      }
      isLocked
      isPrivate
      isOwnerEnterpriseManaged
      visibility
      isArchived
      isInOrganization
      hasIssuesEnabled
      hasAnyTemplates
      slashCommandsEnabled
      viewerCanPush
      isBlankIssuesEnabled
      viewerCanCreateIssues
      viewerInteractionLimitReasonHTML(action: "create an issue")
      viewerIssueCreationPermissions {
        labelable
        milestoneable
        assignable
        triageable
        typeable
        canSetIssueFields
      }
      shortDescriptionHTML
      issueTemplate(filename: $templateFilter) @include(if: $withTemplate) {
        __typename
        name
        filename
        body
        title
        labels(first: 20, orderBy: {field: NAME, direction: ASC}) {
          edges {
            node {
              id
              color
              name
              nameHTML
              description
              url
            }
          }
        }
        assignees(first: 10) {
          edges {
            node {
              ... on Actor {
                __typename
                id
                login
                name
                profileResourcePath
                avatarUrl(size: 64)
              }
            }
          }
        }
        type {
          id
          name
          isEnabled
          description
          color
          pinnedFields {
            __typename
            ... on IssueFieldText {
              id
              name
              dataType
            }
            ... on IssueFieldSingleSelect {
              id
              name
              dataType
            }
            ... on IssueFieldDate {
              id
              name
              dataType
            }
            ... on IssueFieldNumber {
              id
              name
              dataType
            }
            ... on IssueFieldMultiSelect {
              id
              name
              dataType
            }
          }
        }
      }
      issueForm(filename: $templateFilter) @include(if: $withTemplate) {
        __typename
        name
        filename
        title
        elements {
          __typename
          ... on IssueFormElementInput {
            itemId: id
            label
            descriptionHTML
            placeholder
            value
            required
          }
          ... on IssueFormElementTextarea {
            itemId: id
            label
            descriptionHTML
            placeholder
            value
            required
            render
          }
          ... on IssueFormElementMarkdown {
            contentHTML
          }
          ... on IssueFormElementDropdown {
            itemId: id
            label
            descriptionHTML
            options
            required
            multiple
            defaultOptionIndex: default
          }
          ... on IssueFormElementCheckboxes {
            itemId: id
            label
            descriptionHTML
            checkboxOptions: options {
              label
              labelHTML
              required
            }
          }
          ... on IssueFormElementUpload {
            itemId: id
            label
            descriptionHTML
            required
            accept
          }
        }
        labels(first: 20, orderBy: {field: NAME, direction: ASC}) {
          edges {
            node {
              id
              color
              name
              nameHTML
              description
              url
            }
          }
        }
        assignees(first: 10) {
          edges {
            node {
              ... on Actor {
                __typename
                id
                login
                name
                profileResourcePath
                avatarUrl(size: 64)
              }
            }
          }
        }
        projects(first: 20) {
          edges {
            node {
              id
              title
              closed
              number
              url
              viewerCanUpdate
              hasReachedItemsLimit
              __typename
            }
          }
        }
        type {
          id
          name
          isEnabled
          description
          color
          pinnedFields {
            __typename
            ... on IssueFieldText {
              id
              name
              dataType
            }
            ... on IssueFieldSingleSelect {
              id
              name
              dataType
            }
            ... on IssueFieldDate {
              id
              name
              dataType
            }
            ... on IssueFieldNumber {
              id
              name
              dataType
            }
            ... on IssueFieldMultiSelect {
              id
              name
              dataType
            }
          }
        }
      }
      viewerIssueCreationPermissions {
        assignable @include(if: $withAssignees)
        labelable @include(if: $withLabels)
        milestoneable @include(if: $withMilestone)
        triageable @include(if: $withTriagePermission)
        typeable @include(if: $withType)
      }
      suggestedActors(capabilities: [CAN_BE_ASSIGNED], first: 10, loginNames: $assigneeLogins)
        @include(if: $withAssignees) {
        nodes {
          ... on Actor {
            __typename
            id
            login
            name
            profileResourcePath
            avatarUrl(size: 64)
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
      }
      labels(first: 20, names: $labelNames) @include(if: $withLabels) {
        nodes {
          id
          color
          name
          nameHTML
          description
          url
        }
      }
      discussion(number: $discussionNumber) @include(if: $includeDiscussion) {
        formattedBody
        title
        labels(first: 20, orderBy: {field: NAME, direction: ASC}) {
          edges {
            node {
              id
              color
              name
              nameHTML
              description
              url
            }
          }
        }
      }
      milestoneByTitle(title: $milestoneTitle) @include(if: $withMilestone) {
        id
        title
        closed
        dueOn
        progressPercentage
        url
        closedAt
      }
      issueType(name: $type) @include(if: $withType) {
        id
        name
        isEnabled
        description
        color
        pinnedFields {
          __typename
          ... on IssueFieldText {
            id
            name
            dataType
          }
          ... on IssueFieldSingleSelect {
            id
            name
            dataType
          }
          ... on IssueFieldDate {
            id
            name
            dataType
          }
          ... on IssueFieldNumber {
            id
            name
            dataType
          }
          ... on IssueFieldMultiSelect {
            id
            name
            dataType
          }
        }
      }
      owner {
        ... on ProjectV2Owner {
          projectsV2ByNumber(first: 20, numbers: $projectNumbers) @include(if: $withProjects) {
            nodes {
              id
              title
              closed
              number
              url
              viewerCanUpdate
              hasReachedItemsLimit
              __typename
            }
          }
        }
      }
    }
  }
`

export type InternalIssueNewPageProps = {
  urlParameterQueryData: PreloadedQuery<InternalIssueNewPageUrlArgumentsMetadataQuery>
}

export const InternalIssueNewPageWithUrlParams = ({urlParameterQueryData}: InternalIssueNewPageProps) => {
  const repositoryData = usePreloadedQuery<InternalIssueNewPageUrlArgumentsMetadataQuery>(
    InternalIssueNewPageUrlArgumentsMetadata,
    urlParameterQueryData,
  )?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  // RELAY-CLEANUP(InternalIssueNewPageUrlArgumentsMetadataQuery): open-enum boundary cast — the still-Relay
  // preloaded-query data carries `"%future added value"` enum members (e.g. visibility) the SPQ type omits.
  // Remove when InternalIssueNewPageUrlArgumentsMetadataQuery leaves Relay.
  return <IssueNewContent repository={okResult.value as InternalIssueNewPageUrlArgumentsMetadataFragment} />
}
